import { createStorefrontApiClient } from '@shopify/storefront-api-client';

const CART_FRAGMENT = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount {
      amount
      currencyCode
    }
    totalAmount {
      amount
      currencyCode
    }
  }
  lines(first: 100) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            image {
              altText
              url
            }
            price {
              amount
              currencyCode
            }
            product {
              handle
              title
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation CartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        ${CART_FRAGMENT}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_QUERY = `
  query Cart($cartId: ID!) {
    cart(id: $cartId) {
      ${CART_FRAGMENT}
    }
  }
`;

const CART_LINES_ADD_MUTATION = `
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ${CART_FRAGMENT}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ${CART_FRAGMENT}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ${CART_FRAGMENT}
      }
      userErrors {
        field
        message
      }
    }
  }
`;

function assertNoUserErrors(userErrors, operationName) {
  if (!userErrors?.length) {
    return;
  }

  throw new Error(`${operationName}: ${userErrors.map((error) => error.message).join(', ')}`);
}

function assertNoRequestErrors(errors) {
  if (!errors) {
    return;
  }

  const messages = [errors.message, ...(errors.graphQLErrors ?? []).map((error) => error.message ?? String(error))].filter(
    Boolean
  );

  throw new Error(messages.join(', ') || 'Storefront API request failed');
}

export function createShopifyStorefrontClient() {
  const storeDomain = (process.env.GATSBY_SHOPIFY_STORE_URL || '').replace(/^https?:\/\//, '');

  return createStorefrontApiClient({
    apiVersion: '2025-10',
    publicAccessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
    storeDomain,
  });
}

export function normalizeCart(cart) {
  if (!cart) {
    return {
      checkoutUrl: null,
      id: null,
      lineItems: [],
      subtotalAmount: '0',
      totalAmount: '0',
      totalQuantity: 0,
      webUrl: null,
    };
  }

  const lineItems = (cart.lines?.edges ?? []).map(({ node }) => {
    const merchandise = node.merchandise;

    return {
      id: node.id,
      quantity: node.quantity,
      title: merchandise?.product?.title ?? '',
      variant: {
        id: merchandise?.id,
        image: merchandise?.image
          ? {
              altText: merchandise.image.altText,
              src: merchandise.image.url,
            }
          : null,
        price: {
          amount: merchandise?.price?.amount ?? '0',
          currencyCode: merchandise?.price?.currencyCode,
        },
        product: {
          handle: merchandise?.product?.handle,
          title: merchandise?.product?.title,
        },
        selectedOptions: merchandise?.selectedOptions ?? [],
        title: merchandise?.title,
      },
    };
  });

  return {
    checkoutUrl: cart.checkoutUrl,
    id: cart.id,
    lineItems,
    subtotalAmount: cart.cost?.subtotalAmount?.amount ?? '0',
    totalAmount: cart.cost?.totalAmount?.amount ?? '0',
    totalQuantity: cart.totalQuantity ?? 0,
    // Compatibility alias for older checkout.webUrl callers
    webUrl: cart.checkoutUrl,
  };
}

export async function createCart(client, lines = []) {
  const { data, errors } = await client.request(CART_CREATE_MUTATION, {
    variables: {
      input: lines.length ? { lines } : {},
    },
  });

  assertNoRequestErrors(errors);
  assertNoUserErrors(data?.cartCreate?.userErrors, 'cartCreate');

  return normalizeCart(data?.cartCreate?.cart);
}

export async function fetchCart(client, cartId) {
  const { data, errors } = await client.request(CART_QUERY, {
    variables: { cartId },
  });

  assertNoRequestErrors(errors);

  return normalizeCart(data?.cart);
}

export async function addCartLines(client, cartId, lines) {
  const { data, errors } = await client.request(CART_LINES_ADD_MUTATION, {
    variables: { cartId, lines },
  });

  assertNoRequestErrors(errors);
  assertNoUserErrors(data?.cartLinesAdd?.userErrors, 'cartLinesAdd');

  return normalizeCart(data?.cartLinesAdd?.cart);
}

export async function updateCartLines(client, cartId, lines) {
  const { data, errors } = await client.request(CART_LINES_UPDATE_MUTATION, {
    variables: { cartId, lines },
  });

  assertNoRequestErrors(errors);
  assertNoUserErrors(data?.cartLinesUpdate?.userErrors, 'cartLinesUpdate');

  return normalizeCart(data?.cartLinesUpdate?.cart);
}

export async function removeCartLines(client, cartId, lineIds) {
  const { data, errors } = await client.request(CART_LINES_REMOVE_MUTATION, {
    variables: { cartId, lineIds },
  });

  assertNoRequestErrors(errors);
  assertNoUserErrors(data?.cartLinesRemove?.userErrors, 'cartLinesRemove');

  return normalizeCart(data?.cartLinesRemove?.cart);
}
